import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@components/atoms/Typography';
import { Card } from '@components/atoms/Card';
import { Table } from '@components/atoms/Table';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { InputAmount } from '@components/atoms/InputAmount';
import { Checkbox, CheckboxType } from '@components/atoms/Checkbox';
import { useData } from '@hooks/useData';
import { useServicesCart } from '@hooks/useServicesCart';
import { formatPrice } from '@utils/format';
import styles from './ServiceTable.module.css';

export const ServiceTable: FC = () => {
    const { servicesToBook, isLoadingServicesCartTotals } = useData();
    const { services, toggleService, updateServiceAmount, removeService } = useServicesCart();
    const { t } = useTranslation();

    const translations = {
        serviceLabel: t('service-table-component.service.label'),
        quantityLabel: t('service-table-component.quantity.label'),
        priceLabel: t('service-table-component.price.label'),
        durationLabel: t('service-table-component.duration.label'),
    };

    const onlyOneService = servicesToBook?.data?.services?.length === 1;

    const handleOnToggleCheckbox = (serviceUuid: string) => {
        if (!isLoadingServicesCartTotals) {
            toggleService(serviceUuid);
        }
    };

    const handleOnUpdateAmount = (serviceUuid: string, amount: number) => {
        if (!isLoadingServicesCartTotals) {
            updateServiceAmount(serviceUuid, amount);
        }
    };

    const handleOnRemove = (serviceUuid: string) => {
        if (!isLoadingServicesCartTotals) {
            removeService(serviceUuid);
        }
    };

    return (
        <Card>
            <Table
                borderless={true}
                hideHeaderOnMobile={true}
                headerCols={[
                    !onlyOneService ? { classes: 'align-content-start', text: '' } : undefined,
                    { classes: 'text-start', text: translations.serviceLabel },
                    { classes: 'text-center', text: translations.quantityLabel, fit: true },
                    {
                        classes: `${styles['price-col']} text-end`,
                        text: `${translations.priceLabel} / ${translations.durationLabel}`,
                    },
                ].filter((item) => item !== undefined)}
                contentRows={(servicesToBook?.data?.services || []).flatMap((serviceToBook, index) => {
                    return [
                        // service information
                        [
                            !onlyOneService
                                ? {
                                      content: (
                                          <Checkbox
                                              type={CheckboxType.normal}
                                              name={serviceToBook.uuid || ''}
                                              value={serviceToBook.uuid || ''}
                                              checked={
                                                  services?.find((item) => item.uuid === serviceToBook.uuid)?.checked
                                                      ? true
                                                      : false
                                              }
                                              onChange={() => handleOnToggleCheckbox(serviceToBook.uuid || '')}
                                              label={<></>}
                                          />
                                      ),
                                  }
                                : undefined,
                            {
                                content: (
                                    <>
                                        <div className="d-flex justify-content-between align-items-center me-3 my-3">
                                            <div className="d-grid py-2">
                                                <Typography
                                                    color="dark"
                                                    fontSize="14"
                                                    fontWeight="900"
                                                    breakpointsStyles={{ md: { fontSize: '18' } }}>
                                                    {serviceToBook.name}
                                                </Typography>
                                                {serviceToBook.description && (
                                                    <Typography
                                                        classes="mb-2 pe-2"
                                                        color="muted"
                                                        fontSize="14"
                                                        fontWeight="400"
                                                        breakpointsStyles={{ md: { fontSize: '16' } }}>
                                                        {serviceToBook.description}
                                                    </Typography>
                                                )}
                                            </div>
                                            <div className="d-md-none me-3 my-3">
                                                <Typography
                                                    classes="mb-0"
                                                    color="muted"
                                                    fontWeight="400"
                                                    fontSize="16"
                                                    align="right">
                                                    {serviceToBook.duration}
                                                </Typography>
                                            </div>
                                        </div>

                                        <div className="d-flex d-md-none justify-content-between align-items-center me-3 my-3">
                                            <div className="d-flex gap-3">
                                                <ServiceAmount
                                                    uuid={serviceToBook.uuid || ''}
                                                    quantity={
                                                        services?.find((item) => item.uuid === serviceToBook.uuid)?.quantity || 1
                                                    }
                                                    handleOnUpdateAmount={handleOnUpdateAmount}
                                                />
                                                {!onlyOneService && (
                                                    <div className="d-flex gap-2">
                                                        {/* remove */}
                                                        <RoundIcon
                                                            variant={RoundIconVariants.variant2}
                                                            icon="fa-light fa-trash-can"
                                                            onClick={() => handleOnRemove(serviceToBook.uuid || '')}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            <Typography
                                                classes="mb-0"
                                                color="primary"
                                                align="right"
                                                fontWeight="800"
                                                fontSize="20">
                                                {formatPrice(serviceToBook.price)}
                                            </Typography>
                                        </div>
                                    </>
                                ),
                            },
                            // hide 2nd and 3rd cells on mobile
                            {
                                classes: 'd-none d-md-table-cell align-content-start py-4',
                                content: (
                                    <div className="d-flex gap-3">
                                        <ServiceAmount
                                            uuid={serviceToBook.uuid || ''}
                                            quantity={services?.find((item) => item.uuid === serviceToBook.uuid)?.quantity || 1}
                                            handleOnUpdateAmount={handleOnUpdateAmount}
                                        />
                                        {!onlyOneService && (
                                            <div className="d-flex gap-2">
                                                {/* remove */}
                                                <RoundIcon
                                                    variant={RoundIconVariants.variant2}
                                                    icon="fa-light fa-trash-can"
                                                    onClick={() => handleOnRemove(serviceToBook.uuid || '')}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ),
                            },
                            {
                                classes: `${styles['price-col']} d-none d-md-table-cell align-content-start py-4`,
                                content: (
                                    <>
                                        <Typography classes="mb-0" color="primary" fontWeight="800" fontSize="20" align="right">
                                            {formatPrice(serviceToBook.price)}
                                        </Typography>
                                        <Typography classes="mb-0" color="muted" fontWeight="400" fontSize="16" align="right">
                                            {serviceToBook.duration}
                                        </Typography>
                                    </>
                                ),
                            },
                        ].filter((item) => item !== undefined),
                        // line separator (except on last line)
                        index !== (services?.length || 0) - 1 ? [{ colspan: 4, content: <hr /> }] : [],
                    ];
                })}
            />
        </Card>
    );
};

interface ServiceAmountProps {
    uuid: string;
    quantity: number;
    handleOnUpdateAmount: (serviceUuid: string, amount: number) => void;
}

const ServiceAmount: FC<ServiceAmountProps> = ({ uuid, quantity, handleOnUpdateAmount }) => {
    return (
        <InputAmount
            min={1}
            value={quantity === 0 ? 1 : quantity}
            onChange={(value) => handleOnUpdateAmount(uuid, value)}
            size="sm"
        />
    );
};
